import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Subscription } from "rxjs/Subscription";
import { SelectItem } from "primeng/components/common/api";
import * as FileSaver from 'file-saver';
import { AppConstants } from "../core/app.constants";
import { AuthAccessProvider } from "../core/core.auth";
import { BaseComponent, UserContext } from "../core/core.component";
import { MessageProvider } from "../service/message.service";
import { BusyProvider } from "../service/busy.service";
import { ReportService } from "app/service/report.service";
import { AnalisiTicketService, AnalisiTicketRequest, AnalisiTicketResult } from "app/service/analisi-ticket.service";

/** Report disponibili (SIT-1875): ciascuno corrisponde a un calcolo lato server, con le viste che ammette */
class ReportDef {
	id: string;
	label: string;
	/** etichetta del filtro vista, null = report senza scelta della vista */
	viewLabel: string;
	views: SelectItem[];
	defaultView: string;
	/** valori mostrati anche in percentuale sul totale del gruppo */
	percent: boolean;
	colors: string[];
}

class ChartDef {
	type: string;
	data: any;
	options: any;
	height: number;
}

const ESTIMATION = "ESTIMATION_QUALITY";
const LIFE_RANGE = "LIFE_RANGE";
const WORKED = "WORKED_ISSUES";
const ACTIVITY = "ACTIVITY_TYPES";
const CREATED = "CREATED_ISSUES";
const CUSTOM_PERIOD = "custom";
const DEFAULT_PERIOD = "currentYear";
const UNIT_HOURS = "ore";
/** separatore dei tipi issue nel parametro dell'export Excel: i nomi possono contenere virgole */
const ISSUE_TYPES_SEPARATOR = "|";

// colori delle categorie: verde = esito migliore, rosso = peggiore
const ESTIMATION_COLORS = ["#5cb85c", "#9fd3f0", "#337ab7", "#f0ad4e", "#d9534f"];
const LIFE_COLORS = ["#2e7d32", "#5cb85c", "#9ccc65", "#f0ad4e", "#ef6c00", "#d9534f"];
const PALETTE = ["#337ab7", "#5cb85c", "#f0ad4e", "#d9534f", "#5bc0de", "#8e6bb8", "#795548", "#9e9e9e", "#26a69a", "#ec407a",
	"#1a4e7a", "#2e7d32", "#b8860b", "#8b1e1e", "#0e7c99", "#5a3d7a", "#c49a6c", "#455a64", "#9ccc65", "#ff8a65"];

const VIEW_TOTAL: SelectItem = { label: "Complessiva", value: "TOTAL" };
const VIEW_PROJECT: SelectItem = { label: "Per progetto", value: "PROJECT" };
const VIEW_USER: SelectItem = { label: "Per utente", value: "USER" };
const VIEW_ISSUETYPE: SelectItem = { label: "Per tipo issue", value: "ISSUETYPE" };

@Component({
	selector: "app-analisi-ticket",
	templateUrl: "./analisi-ticket.component.html",
	styleUrls: ["./analisi-ticket.component.css"],
})
export class AnalisiTicketComponent extends BaseComponent implements OnInit {

	reports: ReportDef[] = [
		{ id: ESTIMATION, label: "Qualità delle stime", viewLabel: "Vista", views: [VIEW_TOTAL, VIEW_PROJECT], defaultView: VIEW_PROJECT.value, percent: true, colors: ESTIMATION_COLORS },
		{ id: LIFE_RANGE, label: "Durata delle issue", viewLabel: "Vista", views: [VIEW_TOTAL, VIEW_PROJECT], defaultView: VIEW_PROJECT.value, percent: true, colors: LIFE_COLORS },
		{ id: CREATED, label: "Issue create per mese", viewLabel: "Suddivisione", views: [VIEW_ISSUETYPE, VIEW_PROJECT], defaultView: VIEW_ISSUETYPE.value, percent: false, colors: PALETTE },
		{ id: WORKED, label: "Issue lavorate per utente", viewLabel: null, views: [], defaultView: VIEW_USER.value, percent: false, colors: PALETTE },
		{ id: ACTIVITY, label: "Ore per tipo di attività", viewLabel: "Vista", views: [VIEW_TOTAL, VIEW_PROJECT, VIEW_USER], defaultView: VIEW_PROJECT.value, percent: true, colors: PALETTE }
	];
	reportOptions: SelectItem[] = this.reports.map(r => ({ label: r.label, value: r.id }));
	periodOptions: SelectItem[] = [
		{ label: "Ultimi 7 giorni", value: "last7Days" },
		{ label: "Ultimo mese", value: "lastMonth" },
		{ label: "Ultimi 3 mesi", value: "last3Months" },
		{ label: "Ultimi 6 mesi", value: "last6Months" },
		{ label: "Ultimi 12 mesi", value: "last12Months" },
		{ label: "Anno corrente", value: "currentYear" },
		{ label: "Anno precedente", value: "previousYear" },
		{ label: "Personalizzato", value: CUSTOM_PERIOD }
	];
	maxLifeOptions: SelectItem[] = [
		{ label: "Tutte", value: null },
		{ label: "Entro 1 settimana", value: 7 },
		{ label: "Entro 2 settimane", value: 14 },
		{ label: "Entro 1 mese", value: 30 },
		{ label: "Entro 3 mesi", value: 90 },
		{ label: "Entro 6 mesi", value: 180 }
	];
	projectOptions: SelectItem[] = [];
	issueTypeOptions: SelectItem[] = [];
	userOptions: SelectItem[] = [];

	selectedReportId: string = ESTIMATION;
	period: string = DEFAULT_PERIOD;
	dateFrom: Date;
	dateTo: Date;
	projectIds: number[] = [];
	issueTypes: string[] = [];
	users: string[] = [];
	groupBy: string;
	maxLifeDays: number = null;

	result: AnalisiTicketResult = null;
	charts: ChartDef[] = [];
	showPercent: boolean = true;
	loading: boolean = false;
	private reportSubscription: Subscription;

	userContext: UserContext;
	locale: any = AppConstants.ITA_CALENDAR_CONF;

	constructor(
		protected authAccess: AuthAccessProvider,
		protected router: Router,
		protected messageProvider: MessageProvider,
		private analisiTicketService: AnalisiTicketService,
		private reportService: ReportService,
		private busyProvider: BusyProvider,
	) {
		super(authAccess, router, messageProvider);
		this.messageProvider.clearAll();
	}

	ngOnInit() {
		this.userContext = this.authAccess.getUserContext();
		if (this.userContext == null) {
			this.authAccess.invalidate();
			return;
		}
		this.loading = true;
		this.analisiTicketService.getProjects(this.userContext.srmToken).subscribe(
			result => {
				// default di tutti i report: progetti proposti dal server (tutti tranne gli esclusi), tutti i tipi issue, anno corrente
				this.projectOptions = result.projects.map(p => ({ label: p.name, value: p.id }));
				this.projectIds = (result.defaultProjectIds || []).slice();
				this.issueTypeOptions = (result.issueTypes || []).map(t => ({ label: t, value: t }));
				this.issueTypes = this.issueTypeOptions.map(t => t.value);
				this.applyPeriod();
				this.selectReport();
			},
			error => {
				this.loading = false;
				this.handleError(error);
			}
		);
	}

	get selectedReport(): ReportDef {
		return this.reports.find(r => r.id == this.selectedReportId);
	}

	get isWorked(): boolean {
		return this.selectedReportId == WORKED;
	}

	get isLifeRange(): boolean {
		return this.selectedReportId == LIFE_RANGE;
	}

	get isHours(): boolean {
		return this.result != null && this.result.unit == UNIT_HOURS;
	}

	/** le date da/a si modificano solo con il periodo personalizzato, negli altri casi le calcola applyPeriod */
	get isCustomPeriod(): boolean {
		return this.period == CUSTOM_PERIOD;
	}

	/** spiegazione del report per il tooltip dell'icona informativa */
	get notesTooltip(): string {
		return this.result == null ? "" : this.result.notes.map(n => this.escapeHtml(n)).join("<br>");
	}

	/** alla scelta del report si reimpostano la vista e la durata massima, progetti, tipi e periodo restano quelli scelti */
	selectReport() {
		this.groupBy = this.selectedReport.defaultView;
		this.maxLifeDays = null;
		this.loadReport();
	}

	/** calcolo delle date del periodo: i periodi in mesi/giorni sono quelli conclusi, come nei workflow KNIME */
	applyPeriod() {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const year = today.getFullYear();
		const month = today.getMonth();
		switch (this.period) {
			case "last7Days":
				this.dateFrom = new Date(year, month, today.getDate() - 7);
				this.dateTo = new Date(year, month, today.getDate() - 1);
				break;
			case "lastMonth":
				this.setLastMonths(1);
				break;
			case "last3Months":
				this.setLastMonths(3);
				break;
			case "last6Months":
				this.setLastMonths(6);
				break;
			case "last12Months":
				this.setLastMonths(12);
				break;
			case "currentYear":
				this.dateFrom = new Date(year, 0, 1);
				this.dateTo = today;
				break;
			case "previousYear":
				this.dateFrom = new Date(year - 1, 0, 1);
				this.dateTo = new Date(year - 1, 11, 31);
				break;
		}
	}

	private setLastMonths(months: number) {
		const today = new Date();
		this.dateFrom = new Date(today.getFullYear(), today.getMonth() - months, 1);
		this.dateTo = new Date(today.getFullYear(), today.getMonth(), 0);
	}

	private buildRequest(): AnalisiTicketRequest {
		const request = new AnalisiTicketRequest();
		request.report = this.selectedReportId;
		// nessun progetto selezionato = tutti i progetti attivi proposti nel filtro
		request.projectIds = this.projectIds.length > 0 ? this.projectIds : this.projectOptions.map(p => p.value);
		// nessun tipo o tutti i tipi selezionati = nessun filtro, così non si escludono tipi assenti dall'elenco
		request.issueTypes = this.issueTypes.length > 0 && this.issueTypes.length < this.issueTypeOptions.length ? this.issueTypes : null;
		request.users = this.isWorked && !this.allUsersSelected ? this.users : null;
		request.dateFrom = this.formatDate(this.dateFrom);
		request.dateTo = this.formatDate(this.dateTo);
		request.groupBy = this.groupBy;
		request.maxLifeDays = this.isLifeRange ? this.maxLifeDays : null;
		return request;
	}

	/** nessun utente o tutti gli utenti selezionati = nessun filtro */
	private get allUsersSelected(): boolean {
		return this.users.length == 0 || this.users.length >= this.userOptions.length;
	}

	/**
	 * Il filtro Utenti propone gli utenti del report con gli altri filtri: se erano tutti selezionati si selezionano tutti i nuovi,
	 * altrimenti resta la scelta fatta, limitata agli utenti ancora presenti.
	 */
	private updateUserOptions(result: AnalisiTicketResult, allSelected: boolean) {
		this.userOptions = (result.users || []).map(u => ({ label: u.name, value: u.username }));
		const available = this.userOptions.map(u => u.value);
		this.users = allSelected ? available : this.users.filter(u => available.indexOf(u) >= 0);
	}

	private validFilters(): boolean {
		this.messageProvider.clearAll();
		if (this.dateFrom == null || this.dateTo == null) {
			this.messageProvider.notifyWarning("Indicare le date di inizio e fine periodo");
			return false;
		}
		if (this.dateFrom > this.dateTo) {
			this.messageProvider.notifyWarning("La data di inizio periodo non può essere successiva alla data di fine");
			return false;
		}
		return true;
	}

	loadReport() {
		// una richiesta ancora in corso (es. report cambiato durante il calcolo) non deve sovrascrivere quella nuova
		if (this.reportSubscription != null) {
			this.reportSubscription.unsubscribe();
		}
		this.result = null;
		this.charts = [];
		if (!this.validFilters()) {
			this.loading = false;
			return;
		}
		this.loading = true;
		const allUsersSelected = this.allUsersSelected;
		this.reportSubscription = this.analisiTicketService.getReport(this.userContext.srmToken, this.buildRequest()).subscribe(
			response => {
				this.loading = false;
				this.result = response.result;
				if (this.isWorked) {
					this.updateUserOptions(response.result, allUsersSelected);
				}
				this.charts = this.buildCharts(response.result);
			},
			error => {
				this.loading = false;
				this.handleError(error);
			}
		);
	}

	exportExcel() {
		if (!this.validFilters()) {
			return;
		}
		const request = this.buildRequest();
		const parameters = new Object();
		parameters['report'] = request.report;
		parameters['dateFrom'] = request.dateFrom;
		parameters['dateTo'] = request.dateTo;
		parameters['projectIds'] = request.projectIds.join(',');
		parameters['groupBy'] = request.groupBy;
		if (request.users != null) {
			parameters['users'] = request.users.join(',');
		}
		if (request.issueTypes != null) {
			parameters['issueTypes'] = request.issueTypes.join(ISSUE_TYPES_SEPARATOR);
		}
		if (request.maxLifeDays != null) {
			parameters['maxLifeDays'] = request.maxLifeDays;
		}
		this.busyProvider.showBusyIndicator();
		this.reportService.exportReport(this.userContext.srmToken, 'com.netsitech.srm.excel.generator.AnalisiTicketReportGenerator', parameters).subscribe(
			data => {
				this.busyProvider.hideBusyIndicator();
				const filename = this.selectedReport.label.replace(/à/g, "a").replace(/[^A-Za-z0-9]+/g, "_") + "_" + request.dateFrom + "_" + request.dateTo + ".xlsx";
				FileSaver.saveAs(new Blob([data], { type: "application/vnd.ms-excel" }), filename);
			},
			error => {
				// la risposta attesa e' un blob, gli errori json non vengono mappati: si mostra l'errore cosi' com'e'
				this.busyProvider.hideBusyIndicator();
				this.messageProvider.notifyError(error);
			}
		);
	}

	percent(value: number, total: number): number {
		return total > 0 ? Math.round(value / total * 1000) / 10 : 0;
	}

	/** ore con un decimale, conteggi interi */
	formatValue(value: number): string {
		return this.isHours ? (Math.round(value * 10) / 10).toFixed(1).replace(".", ",") : String(Math.round(value));
	}

	private buildCharts(result: AnalisiTicketResult): ChartDef[] {
		if (result == null || result.groups.length == 0 || result.total == 0) {
			return [];
		}
		const report = this.selectedReport;
		if (report.id == CREATED) {
			return [this.buildTimeBar(result, report.colors)];
		}
		if (!report.percent) {
			return [this.buildStackedBar(result, report.colors, false, this.capitalize(result.unit))];
		}
		if (result.groups.length == 1) {
			return [this.buildRadar(result, report.colors)];
		}
		return [this.buildStackedBar(result, report.colors, true, "% " + result.unit)];
	}

	/** vista complessiva: radar con la percentuale per categoria, come nei workflow KNIME */
	private buildRadar(result: AnalisiTicketResult, colors: string[]): ChartDef {
		const group = result.groups[0];
		return {
			type: "radar",
			height: 380,
			data: {
				labels: result.categories,
				datasets: [{
					label: "% " + result.unit + " (" + this.formatValue(group.total) + " totali)",
					data: group.values.map(v => this.percent(v, group.total)),
					backgroundColor: "rgba(92, 184, 92, 0.4)",
					borderColor: "#2e7d32",
					pointBackgroundColor: result.categories.map((c, i) => colors[i % colors.length]),
					pointRadius: 5
				}]
			},
			options: {
				responsive: true,
				legend: { position: "bottom" },
				scale: { ticks: { beginAtZero: true, min: 0, max: 100, stepSize: 20 } },
				tooltips: {
					callbacks: {
						label: (item) => result.categories[item.index] + ": " + item.yLabel + "% (" + this.formatValue(group.values[item.index]) + " " + result.unit + ")"
					}
				}
			}
		};
	}

	/** barre orizzontali impilate, una per gruppo (progetto o utente); in percentuale per le distribuzioni */
	private buildStackedBar(result: AnalisiTicketResult, colors: string[], asPercent: boolean, axisLabel: string): ChartDef {
		return {
			type: "horizontalBar",
			height: Math.max(200, 60 + result.groups.length * 28),
			data: {
				labels: result.groups.map(g => g.name + " (" + this.formatValue(g.total) + ")"),
				datasets: result.categories.map((category, i) => ({
					label: category,
					backgroundColor: colors[i % colors.length],
					data: result.groups.map(g => asPercent ? this.percent(g.values[i], g.total) : g.values[i])
				}))
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				legend: { position: "bottom" },
				scales: {
					xAxes: [{ stacked: true, ticks: { beginAtZero: true, max: asPercent ? 100 : undefined }, scaleLabel: { display: true, labelString: axisLabel } }],
					yAxes: [{ stacked: true }]
				},
				tooltips: {
					callbacks: {
						label: (item) => {
							const group = result.groups[item.index];
							const value = group.values[item.datasetIndex];
							return result.categories[item.datasetIndex] + ": " + this.formatValue(value) + " " + result.unit + (asPercent ? " (" + item.xLabel + "%)" : "");
						}
					}
				}
			}
		};
	}

	/** barre verticali impilate, una per periodo (mese o giorno), per le issue create */
	private buildTimeBar(result: AnalisiTicketResult, colors: string[]): ChartDef {
		return {
			type: "bar",
			height: 400,
			data: {
				labels: result.groups.map(g => g.name),
				datasets: result.categories.map((category, i) => ({
					label: category,
					backgroundColor: colors[i % colors.length],
					data: result.groups.map(g => g.values[i])
				}))
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				legend: { position: "bottom" },
				scales: {
					xAxes: [{ stacked: true }],
					yAxes: [{ stacked: true, ticks: { beginAtZero: true, precision: 0 }, scaleLabel: { display: true, labelString: "Issue" } }]
				},
				tooltips: {
					mode: "index",
					filter: (item) => item.yLabel > 0,
					callbacks: {
						footer: (items) => "Totale: " + this.formatValue(result.groups[items[0].index].total)
					}
				}
			}
		};
	}

	private capitalize(text: string): string {
		return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
	}

	private escapeHtml(text: string): string {
		return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
	}

	private formatDate(date: Date): string {
		const pad = (n: number) => (n < 10 ? "0" : "") + n;
		return date.getFullYear() + "-" + pad(date.getMonth() + 1) + "-" + pad(date.getDate());
	}
}



// WEBPACK FOOTER //
// ./src/app/analisi-ticket/analisi-ticket.component.ts